import { Box, Button, Stack, Typography } from '@mui/material';
import { SvgIcon } from '@mui/material';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';

import { useModal } from 'components/shared/modal';
import { JobProgressStatus } from 'const/JobProgress';
import useJobProgressLoader from 'hooks/useJobProgressLoader';
import { useSequentialComponents } from 'hooks/useSequentialComponents';
import { AssignIp, RemoveIp, AssignPrivateIp } from 'modules/onechip-details/meerkat/components/OperationModal';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MeerkatService from 'services/meerkat/Meerkat';

const AssignIpService = ({ type, data, modal, ...restProps }) => {
  const { meerkatPublicVpnEnabled = false, meerkatPrivateVpnEnabled = false } = data;

  let sequenceComponents = [];

  if (type === 'private' && !meerkatPrivateVpnEnabled) {
    sequenceComponents = [() => <AssignPrivateIp type={type} data={data} modal={modal} {...restProps} />];
  } else if (type === 'public') {
    sequenceComponents = [
      ({ next, ...props }) => <AssignIp onSubmit={next} modal={modal} {...props} />,
      () => <AssignmentProgressLoader type={type} data={data} modal={modal} {...restProps} />,
    ];
  } else {
    sequenceComponents = [() => <AssignmentProgressLoader type={type} data={data} modal={modal} {...restProps} />];
  }

  const flow = useSequentialComponents(sequenceComponents);

  return flow;
};

const AssignmentProgressLoader = (props) => {
  const { filters, data, type, onComplete, modal } = props;

  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const handleOnComplete = () => {
    onComplete?.();
    modal.closeModal();
  };

  const { start, Loader } = useJobProgressLoader(MeerkatService.assignIp, {
    onComplete: (result) => {
      if (result.status === JobProgressStatus.Finished) {
        enqueueSnackbar(
          t('views.onechip_cards.actions.meerkat.assign_private.assign_operational.message.success', {
            val: result?.data?.processedCount,
          }),
          { variant: 'success' }
        );
        handleOnComplete();
      } else {
        enqueueSnackbar(`An error occured`, { variant: 'error' });
        handleOnComplete();
      }
    },
  });

  useEffect(() => {
    executeAssignment();
  }, []);

  const executeAssignment = () => {
    start({
      meerkatPublicIp: type === 'public',
      meerkatPlanId: data.planId,
      eid: data.eid,
      clientId: data.clientId,
      ...filters,
    });
  };

  return <Loader size={84} />;
};

const RemoveIpService = ({ type, data, modal, ...restProps }) => {
  const sequenceComponents = [
    ({ next, ...props }) => <RemoveIp onSubmit={next} modal={modal} {...props} />,
    () => <RemovalProgressLoader type={type} data={data} modal={modal} {...restProps} />,
  ];

  const flow = useSequentialComponents(sequenceComponents);

  return flow;
};

const RemovalProgressLoader = (props) => {
  const { data, onComplete, modal } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleOnComplete = () => {
    onComplete?.();
    modal.closeModal();
  };

  const { start, Loader } = useJobProgressLoader(MeerkatService.removeIp, {
    onComplete: (result) => {
      if (result.status === JobProgressStatus.Finished) {
        enqueueSnackbar(
          t('views.onechip_cards.actions.meerkat.remove_ip.remove_operational.message.success', {
            val: result?.data?.processedCount,
          }),
          { variant: 'success' }
        );
        handleOnComplete();
      } else {
        enqueueSnackbar(`An error occured`, { variant: 'error' });
        handleOnComplete();
      }
    },
  });

  useEffect(() => {
    executeAssignment();
  }, []);

  const executeAssignment = () => {
    start({
      meerkatPublicIp: data.meerkatPublic,
      meerkatPlanId: data.planId,
      eid: data.eid,
      clientId: data.clientId,
    });
  };

  return <Loader size={84} />;
};

const AssignContainer = ({ data = {}, type, children, onComplete = () => {} }) => {
  const modal = useModal({ stacked: false });

  const onAssign = (event, filters = {}) => {
    event.preventDefault();
    event.stopPropagation();
    modal.openModal(AssignIpService, {
      filters,
      data,
      type,
      onComplete,
    });
  };

  const onRemove = (event) => {
    event.preventDefault();
    event.stopPropagation();
    modal.openModal(RemoveIpService, {
      data,
      type,
      onComplete,
    });
  };
  return (
    <>
      {children({
        onAssign,
        onRemove,
        isLoadingRemove: false,
        isLoadingAssign: false,
      })}
    </>
  );
};

export const RemoveListItemContainer = ({ data, modal, onComplete }) => {
  return (
    <AssignContainer modal={modal} data={data} type={null} onComplete={onComplete}>
      {({ onRemove }) => (
        <Button onClick={onRemove} sx={{ height: '16px', whiteSpace: 'nowrap' }}>
          <Typography variant="secondary" color="text.secondary">
            Remove App
          </Typography>
          <SvgIcon component={TrashIcon} inheritViewBox />
        </Button>
      )}
    </AssignContainer>
  );
};

const AssignListItemContainer = ({ modal, data, type, onComplete }) => {
  const isAssigned = data.statusCode === 'ASSIGNED';
  const { value } = useMemo(() => {
    const defaultValue = {
      value: 'N/A',
    };
    try {
      const resultByType = {
        public: (data) => {
          let value = data.publicIp;
          if (!data.meerkatPublic) {
            value = 'N/A';
          }
          return { value };
        },
        private: (data) => {
          let value = data.privateIp;
          if (data.meerkatPublic) {
            value = 'N/A';
          }
          return { value };
        },
      };
      const result = resultByType[type](data);
      return result;
    } catch (e) {
      return defaultValue;
    }
  }, [data]);
  return (
    <AssignContainer modal={modal} data={data} type={type} onComplete={onComplete}>
      {() => (
        <>
          {isAssigned ? (
            <Stack direction={'row'}>
              <Box>{value}</Box>
            </Stack>
          ) : (
            <Typography color={type === 'public' ? 'error' : ''}>
              {type === 'public' ? <>Error</> : <>N/A</>}
            </Typography>
          )}
        </>
      )}
    </AssignContainer>
  );
};

export const AssignButtonContainer = AssignContainer;

export default AssignListItemContainer;
